// Properties.js
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LocationFilters from "../components/LocationFilters";
import SortOptions from "../components/SortOptions";
import PropertyCard from "../components/PropertyCard";
import AdvancedFilters from "../components/AdvancedFilters";
import ShareButton from "../components/ShareButton";
import Spinner from "../components/Spinner";

const LIMITE = 20;

const Properties = () => {
    const [propiedades, setPropiedades] = useState([]);
    const [localizaciones, setLocalizaciones] = useState([]);
    const [filtroLocalizacion, setFiltroLocalizacion] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [calculando, setCalculando] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [orden, setOrden] = useState("");
    const [ordenDireccion, setOrdenDireccion] = useState("asc");
    const [mostrarOpciones, setMostrarOpciones] = useState(false);
    const [favoritos, setFavoritos] = useState([]);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [verificacionLoginCompletada, setVerificacionLoginCompletada] = useState(false);
    const [rangoPrecio, setRangoPrecio] = useState({ min: 0, max: 1000000 });

    const [filtrosAvanzados, setFiltrosAvanzados] = useState({
        precioMin: "",
        precioMax: "",
        superficieMin: "",
        superficieMax: "",
        habitacionesMin: "",
        habitacionesMax: "",
        banosMin: "",
        banosMax: "",
        soloIA: false,
        esAnalizable: false,
    });

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        setFiltroLocalizacion(
            params.get("localizaciones")?.split(",").map(Number).filter(n => !isNaN(n)) || []
        );
        setOffset(parseInt(params.get("offset") || "0", 10));
        setOrden(params.get("orden") || "");
        setOrdenDireccion(params.get("direccion") || "asc");

        const initialParamLoad = {
            precioMin: params.get("precioMin") || "",
            precioMax: params.get("precioMax") || "",
            superficieMin: params.get("superficieMin") || "",
            superficieMax: params.get("superficieMax") || "",
            habitacionesMin: params.get("habitacionesMin") || "",
            habitacionesMax: params.get("habitacionesMax") || "",
            banosMin: params.get("banosMin") || "",
            banosMax: params.get("banosMax") || "",
            soloIA: params.get("soloIA") === "true",
            esAnalizable: params.get("esAnalizable") === "true"
        };

        setFiltrosAvanzados(initialParamLoad);
    }, []);

    useEffect(() => {
        const fetchLocalizaciones = async () => {
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/localizaciones/agrupadas`);
                const data = await res.json();
                setLocalizaciones(data);
            } catch (err) {
                console.error("Error cargando localizaciones agrupadas:", err);
            }
        };

        const fetchRangoPrecio = async () => {
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/rango-precios`);
                const data = await res.json();
                if (data.min !== undefined && data.max !== undefined) {
                    setRangoPrecio({ min: data.min, max: data.max });
                }
            } catch (err) {
                console.error("Error obteniendo rango de precio:", err);
            }
        };

        fetchLocalizaciones();
        fetchRangoPrecio();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const soloFavoritas = params.get("solo_favoritas") === "true";

        fetch(`${window.REACT_APP_API_ENDPOINT}/api/current-user`, { credentials: "include" })
            .then((res) => res.json())
            .then((data) => {
                if (data.user) {
                    setIsLoggedIn(true);
                    if (soloFavoritas) {
                        fetch(`${window.REACT_APP_API_ENDPOINT}/starred`, { credentials: "include" })
                            .then(res => res.json())
                            .then(setFavoritos)
                            .catch((err) => console.error("Error cargando favoritos:", err));
                    }
                    setVerificacionLoginCompletada(true);
                } else {
                    if (soloFavoritas) {
                        params.delete("solo_favoritas");
                        navigate({ search: params.toString() }, { replace: true });
                    } else {
                        setVerificacionLoginCompletada(true);
                    }
                }
            })
            .catch((err) => {
                console.error("Error comprobando usuario:", err);
                if (soloFavoritas) {
                    params.delete("solo_favoritas");
                    navigate({ search: params.toString() }, { replace: true });
                } else {
                    setVerificacionLoginCompletada(true);
                }
            });
    }, [location.search]);

    useEffect(() => {
        if (verificacionLoginCompletada) {
            cargarPropiedades(offset > 0);
        }
    }, [location.search, verificacionLoginCompletada]);

    const aplicarFiltrosAvanzados = (filtrosAplicados) => {
        const params = new URLSearchParams(location.search);
        params.set("offset", "0");

        // Filtros avanzados
        Object.entries(filtrosAplicados).forEach(([key, value]) => {
            if (key !== "localizaciones") {
                if (value !== "" && value !== false) {
                    params.set(key, value);
                } else {
                    params.delete(key);
                }
            }
        });

        if (filtroLocalizacion.length > 0) {
            params.set("localizaciones", filtroLocalizacion.join(","));
        } else {
            params.delete("localizaciones");
        }

        navigate({ search: params.toString() });
    };


    const cargarPropiedades = async (append = false) => {
        if (!append) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        
        const params = new URLSearchParams({ limit: LIMITE, offset });
        if (filtroLocalizacion.length > 0) params.set("localizaciones", filtroLocalizacion.join(","));
        if (orden) {
            params.set("orden", orden);
            params.set("direccion", ordenDireccion);
        }
        if (location.search.includes("solo_favoritas=true")) {
            params.set("solo_favoritas", "true");
        }

        // Añadir filtros opcionales
        Object.entries(filtrosAvanzados).forEach(([key, value]) => {
            if (value !== "" && value !== false) {
                params.set(key.replace(/[A-Z]/g, m => "_" + m.toLowerCase()), value);
            }
        });

        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades?${params}`, {
                credentials: "include"
            });
            const data = await res.json();
            setPropiedades((prev) => append ? [...prev, ...data] : data);
            setHasMore(data.length === LIMITE);
        } catch (err) {
            console.error("Error cargando propiedades:", err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const limpiarFiltros = () => {
        navigate({ search: "" });
        setFiltrosAvanzados({
            precioMin: "",
            precioMax: "",
            superficieMin: "",
            superficieMax: "",
            habitacionesMin: "",
            habitacionesMax: "",
            banosMin: "",
            banosMax: "",
            soloIA: false,
            esAnalizable: false,
        });
        setFiltroLocalizacion([]);
    };

    const calcularReforma = async (id) => {
        setCalculando(true);
        try {
            await fetch(`${window.REACT_APP_API_ENDPOINT}/calcular-reforma/${id}`, {
                credentials: "include",
                method: "POST"
            });
            navigate(`/propiedad/${id}`);
        } catch (err) {
            console.error("Error calculando la reforma:", err);
        } finally {
            setCalculando(false);
        }
    };

    const toggleStarred = async (propertyId, actualmenteMarcada) => {
        try {
            const method = actualmenteMarcada ? "DELETE" : "POST";
            await fetch(`${window.REACT_APP_API_ENDPOINT}/starred/${propertyId}`, {
                method,
                credentials: "include"
            });

            setFavoritos((prev) =>
                actualmenteMarcada
                    ? prev.filter((id) => id !== propertyId)
                    : [...prev, propertyId]
            );
        } catch (err) {
            console.error("Error actualizando favoritos:", err);
        }
    };

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-3xl font-bold mb-4 text-gray-800">Listado de Propiedades</h1>

            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <button
                        className="bg-gray-300 text-sm text-gray-700 px-3 py-1 rounded hover:bg-gray-400"
                        onClick={() => setMostrarOpciones((prev) => !prev)}
                    >
                        Opciones de búsqueda
                    </button>

                    {isLoggedIn && (
                        <button
                            className="bg-yellow-300 text-sm text-yellow-800 px-3 py-1 rounded hover:bg-yellow-400"
                            onClick={() => {
                                const params = new URLSearchParams(location.search);
                                const yaFavoritas = params.get("solo_favoritas") === "true";
                                if (yaFavoritas) {
                                    params.delete("solo_favoritas");
                                } else {
                                    params.set("solo_favoritas", "true");
                                }
                                params.set("offset", "0");
                                navigate({ search: params.toString() });
                            }}
                        >
                            {location.search.includes("solo_favoritas=true") ? "Ver todas" : "Mostrar solo favoritas"}
                        </button>
                    )}
                </div>

                <ShareButton text="Compartir búsqueda" />
            </div>

            {mostrarOpciones && (
                <div className="mt-4 space-y-4">
                    <LocationFilters
                        localizaciones={localizaciones}
                        filtroLocalizacion={filtroLocalizacion}
                        setFiltroLocalizacion={setFiltroLocalizacion}
                    />
                    <AdvancedFilters
                        filtros={filtrosAvanzados}
                        setFiltros={setFiltrosAvanzados}
                        rangoPrecio={rangoPrecio}
                        filtroLocalizacion={filtroLocalizacion}
                        onAplicar={aplicarFiltrosAvanzados}
                    />
                    <SortOptions
                        orden={orden}
                        ordenDireccion={ordenDireccion}
                        cambiarOrden={(campo) => {
                            const nuevaDireccion = campo === orden && ordenDireccion === "asc" ? "desc" : "asc";
                            const params = new URLSearchParams(location.search);
                            params.set("orden", campo);
                            params.set("direccion", nuevaDireccion);
                            params.set("offset", "0");
                            navigate({ search: params.toString() });
                        }}
                    />
                    <button
                        className="mt-2 text-sm text-red-600 hover:underline"
                        onClick={limpiarFiltros}
                    >
                        Limpiar filtros y orden
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    <div className="col-span-full flex items-center justify-center py-12">
                        <Spinner 
                            size="lg" 
                            text="Cargando propiedades..." 
                            color="blue"
                        />
                    </div>
                ) : (
                    propiedades.map((prop) => (
                        <PropertyCard
                            key={prop.id}
                            prop={prop}
                            navigate={navigate}
                            calcularReforma={calcularReforma}
                            calcularIndicadores={() => {}}
                            calculando={calculando}
                            esFavorita={favoritos.includes(prop.id)}
                            toggleStarred={toggleStarred}
                            isLoggedIn={isLoggedIn}
                        />
                    ))
                )}
            </div>

            {hasMore && (
                <div className="mt-6 text-center">
                    <button
                        className="bg-gray-800 text-white px-6 py-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => {
                            const nuevoOffset = offset + LIMITE;
                            const params = new URLSearchParams(location.search);
                            params.set("offset", nuevoOffset.toString());
                            navigate({ search: params.toString() });
                        }}
                        disabled={loadingMore}
                    >
                        {loadingMore ? (
                            <div className="flex items-center justify-center">
                                <Spinner size="sm" color="white" />
                                <span className="ml-2">Cargando...</span>
                            </div>
                        ) : (
                            "Ver más propiedades"
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default Properties;
